import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { uniquePromoCodesFormInitialValues } from '../form';
import { PromoCodeRewardPicker } from '../promoCodeRewardPicker/PromoCodeRewardPicker';

import { promoCodeService } from 'v2/api/promoCodeService';
import { PromoCodeType } from 'v2/constants';
import { UniquePromoCodeForm } from 'v2/types/PromoCodeModel';
import { handleHttpError } from 'v2/utils';

interface AddUniquePromoCodesDialogProps {
  isOpen: boolean;
  onClose: VoidFunction;
  onRevalidate: VoidFunction;
}

export const AddUniquePromoCodesDialog: FC<AddUniquePromoCodesDialogProps> = ({ isOpen, onClose, onRevalidate }) => {
  const { trigger, isMutating } = useSWRMutation(
    'createUniquePromoCodesGroup',
    promoCodeService.createUniquePromoCodesGroup,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('Promo codes group has been created successfully');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<UniquePromoCodeForm>({
    initialValues: uniquePromoCodesFormInitialValues,
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const handleRewardTypeChange = (value: PromoCodeType) => {
    formik.setFieldValue('type', value);
    formik.setFieldValue('value', '');
  };

  const handleRewardValueChange = (value: string) => {
    formik.setFieldValue('value', value);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Unique Promo Codes Group</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="groupName"
            value={formik.values.groupName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Group Name"
            required
          />

          <PromoCodeRewardPicker
            type={formik.values.type}
            onTypeChange={handleRewardTypeChange}
            value={formik.values.value}
            onValueChange={handleRewardValueChange}
          />

          <TextField
            name="promocodesCount"
            value={formik.values.promocodesCount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Number Of Promo Codes"
            type="number"
            required
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button loading={isMutating} type="submit" variant="contained">
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
